import type { ComponentType, ReactNode } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type TabBarItem = {
  id: string;
  label: ReactNode;
  href: string;
  /** Binoculars for the watchlist, the bell only for alerts. */
  icon: ComponentType<IconProps>;
  /** A small count on the icon (new matches); formatted by the caller, e.g. "99+". */
  badge?: ReactNode;
};

export type TabBarProps = {
  items: TabBarItem[];
  /** The current section's id. */
  value: string;
  /** Accessible name of the bar, localised by the caller. */
  label: string;
  /** Pin to the bottom of the screen below md (the real app); off in the showcase. */
  fixed?: boolean;
  className?: string;
};

/** The phone app's bottom navigation: three to five sections, icon above label, 72px tall. */
export function TabBar({ items, value, label, fixed = false, className }: TabBarProps) {
  return (
    <nav
      aria-label={label}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      className={cx(
        'border-t border-jc-border bg-jc-surface',
        fixed && 'fixed inset-x-0 bottom-0 z-(--jc-z-tabbar) md:hidden',
        className,
      )}
    >
      <ul className="grid h-18 auto-cols-fr grid-flow-col">
        {items.map((item) => {
          const active = item.id === value;
          return (
            <li key={item.id} className="flex">
              <a
                href={item.href}
                aria-current={active ? 'page' : undefined}
                className={cx(
                  'flex flex-1 flex-col items-center justify-center gap-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-jc-focus',
                  active ? 'font-semibold text-jc-ink' : 'text-jc-ink-secondary',
                )}
              >
                <span
                  className={cx(
                    'relative inline-flex h-10 w-14 items-center justify-center rounded-jc-control',
                    active && 'bg-jc-selected text-jc-on-selected',
                  )}
                >
                  <Icon icon={item.icon} size="md" weight={active ? 'fill' : 'bold'} />
                  {item.badge != null ? (
                    <span className="absolute -top-1 right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-jc-ink px-1 text-xs font-semibold tabular-nums text-jc-on-solid">
                      {item.badge}
                    </span>
                  ) : null}
                </span>
                <span className="max-w-full truncate px-1">{item.label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
